import { Injectable } from "@angular/core";
import { AssociatedImage, ConsistencyIssuesToUpdateDto } from "@shared/models/common";
import { FairBalanceIssue, FairBalanceIssueReference, FairBalanceIssueStandalone } from "./model/fair-balance-issue.models";
import { EmbeddedObjectAnnotation } from "@shared/models/embedded-object-annotation";
import { ConsistencyIssue, ConsistencyIssueAssocImage, ConsistencyIssueCatImage, ConsistencyIssueClaim, ConsistencyIssueImageClaim, ConsistencyIssueImageReference, ConsistencyIssueReference } from "./model/consistency-issue.models";
import { NgRedux } from "@shared/redux/redux-compatibility.service";
import { AppState } from "@shared/redux/store";
import { ComplianceIssue } from "./model/compliance-issue.models";

@Injectable({
  providedIn: 'root',
})
export class IssuesFactory {

    constructor(private ngRedux: NgRedux<AppState>) {}

    
    createIssuesPayload(phraseId: number, imageId: number, docId: number, isPrecheck: boolean = true): ConsistencyIssuesToUpdateDto | null {

            let anchorsToUpdate = new ConsistencyIssuesToUpdateDto();
            anchorsToUpdate.doc_id = docId;
            anchorsToUpdate.is_precheck = isPrecheck;
            anchorsToUpdate.phrase_id = phraseId;
            anchorsToUpdate.image_id = imageId;
            anchorsToUpdate.remove = {
                phrase_reference_consistency_check_results: [],
                phrase_claim_consistency_check_results: [],
                phrase_fair_balance_analysis_results: [],
                phrase_general_compliance_analysis_results: [],
                phrase_assoc_image_cat_image_consistency_check_results: [],
                phrase_assoc_image_claim_consistency_check_results: [],
                phrase_assoc_image_reference_consistency_check_results: [],
                image_claim_consistency_check_results: [],
                image_reference_consistency_check_results: [],
                image_cat_image_consistency_check_results: []
            },
                anchorsToUpdate.convert = {
                    anchors: []
                }
                anchorsToUpdate.add = {
                    phrase_reference_consistency_check_results: []
                }
            return anchorsToUpdate;
    }

    createEmptyIssuesPayloadLike(current: ConsistencyIssuesToUpdateDto | null): ConsistencyIssuesToUpdateDto | null {
        if (current == null) {
            return null;
        }
        const payload = this.createIssuesPayload(current.phrase_id, current.image_id, current.doc_id, current.is_precheck);
        payload.is_table = current.is_table;
        return payload;
    }

    createFairBalanceIssue(fairBalanceIssue: any, embeddedObjectAnnotation: EmbeddedObjectAnnotation) : FairBalanceIssue {

        console.log("Creating fair balance issue from dto: ", fairBalanceIssue);   
        switch(fairBalanceIssue.checkType) {
            
            case 'REFERENCE':
                return new FairBalanceIssueReference(fairBalanceIssue.issueId,
                    fairBalanceIssue.explanation,
                    fairBalanceIssue.recommendations,  
                    fairBalanceIssue.category,
                    fairBalanceIssue.checkType,
                    'reference-document-icon',
                    'translate.checkType.fair-balance.' + fairBalanceIssue.checkType,
                    embeddedObjectAnnotation)
            
            case 'STANDALONE':
            
                return new FairBalanceIssueStandalone(fairBalanceIssue.issueId,
                    fairBalanceIssue.explanation,
                    fairBalanceIssue.recommendations,  
                    fairBalanceIssue.category,
                    fairBalanceIssue.checkType,
                    'standalone-issue-icon',
                    'translate.checkType.fair-balance.' + fairBalanceIssue.checkType,
                    fairBalanceIssue.relatedPhraseIds,
                    fairBalanceIssue.relatedPhraseTxts); 
                    
            }    
    }

    createConsistencyIssue(consistencyIssue: any, embeddedObjectAnnotation: EmbeddedObjectAnnotation, isImage: boolean, parentConsistencyIssue: ConsistencyIssueAssocImage=null):  ConsistencyIssue {

        switch(consistencyIssue.consistencyCheckType) {
            case 'REFERENCE': {
                if (isImage || parentConsistencyIssue != null) {
                    return new ConsistencyIssueImageReference(
                    consistencyIssue.issueId,
                    consistencyIssue.explanation,
                    consistencyIssue.category,
                    consistencyIssue.reason,
                    consistencyIssue.consistencyCheckType,
                    consistencyIssue.conclusion,
                    consistencyIssue.convertible ?? false,
                    'reference-document-icon',
                    'translate.checkType.checked.' + consistencyIssue.consistencyCheckType,
                    consistencyIssue.refDocAnchorId,
                    consistencyIssue.refDocAnchorPhraseId,
                    consistencyIssue.catPhraseTxt, 
                    consistencyIssue.refDocId,
                    embeddedObjectAnnotation,
                    parentConsistencyIssue
            )
                }
                return new ConsistencyIssueReference(
                    consistencyIssue.issueId,
                    consistencyIssue.explanation,
                    consistencyIssue.category,
                    consistencyIssue.reason,
                    consistencyIssue.consistencyCheckType,
                    consistencyIssue.conclusion,
                    consistencyIssue.convertible ?? false,
                    'reference-document-icon',
                    'translate.checkType.checked.' + consistencyIssue.consistencyCheckType,
                    consistencyIssue.refDocAnchorId,
                    consistencyIssue.refDocAnchorPhraseId,
                    consistencyIssue.catPhraseTxt, 
                    consistencyIssue.refDocId,
                    embeddedObjectAnnotation,
                    null
            )
        }
            case 'CLAIM': {
                let catVersionId;
                if (this.ngRedux.getState().curCat != null) {
                    
                    catVersionId = this.ngRedux.getState().curCat.catVersionId;
                } else if (this.ngRedux.getState().curRevDoc != null) {
                    
                    catVersionId = this.ngRedux.getState().curRevDoc.catVersionId;
                } 
                if (isImage || parentConsistencyIssue != null) {
                    return new ConsistencyIssueImageClaim(
                    consistencyIssue.issueId,
                    consistencyIssue.explanation,
                    consistencyIssue.category,
                    consistencyIssue.reason,
                    consistencyIssue.consistencyCheckType,
                    consistencyIssue.conclusion,
                    consistencyIssue.convertible ?? false,
                    'fa-regular fa-rectangle-list',
                    'translate.checkType.checked.' + consistencyIssue.consistencyCheckType,
                    embeddedObjectAnnotation,
                    parentConsistencyIssue
                    )
                } else {
                    return new ConsistencyIssueClaim(
                    consistencyIssue.issueId,
                    consistencyIssue.explanation,
                    consistencyIssue.category,
                    consistencyIssue.reason,
                    consistencyIssue.consistencyCheckType,
                    consistencyIssue.conclusion,
                    consistencyIssue.convertible ?? false,
                    'fa-regular fa-rectangle-list',
                    'translate.checkType.checked.' + consistencyIssue.consistencyCheckType,
                    consistencyIssue.catPhraseId,
                    consistencyIssue.catPhraseTxt,
                    catVersionId
                    )
                }
            }
            case 'CAT_IMAGE':
            case 'CAT_TABLE':
                let catVersionId;
                if (this.ngRedux.getState().curCat != null) {
                    
                    catVersionId = this.ngRedux.getState().curCat.catVersionId;
                } else if (this.ngRedux.getState().curRevDoc != null) {
                    
                    catVersionId = this.ngRedux.getState().curRevDoc.catVersionId;
                } 
                        return new ConsistencyIssueCatImage(
                        consistencyIssue.issueId,
                        consistencyIssue.explanation,
                        consistencyIssue.category,
                        consistencyIssue.reason,
                        consistencyIssue.consistencyCheckType,
                        consistencyIssue.conclusion,
                        consistencyIssue.convertible ?? false,
                        'far fa-images',
                        'translate.checkType.checked.' + consistencyIssue.consistencyCheckType,
                        consistencyIssue.catImageId,
                        catVersionId,
                        embeddedObjectAnnotation,
                        parentConsistencyIssue
                        )
            case 'ASSOC_IMAGE':

                        let reason = '';
                        if (consistencyIssue.figureType == 'TABLE') {
                            reason = 'Inconsistent Associated Table with ID: ' + consistencyIssue.assocImageId;
                        } else {
                            reason = 'Inconsistent Associated Image with ID: ' + consistencyIssue.assocImageId;
                        }

                        return new ConsistencyIssueAssocImage(
                        null,
                        null,
                        null,
                        reason,
                        'ASSOC_IMAGE',
                        null,
                        false,
                        'assoc-image-icon',
                        'translate.checkType.checked.' + consistencyIssue.consistencyCheckType + '.' + consistencyIssue.figureType,
                        consistencyIssue.assocImageId,
                        consistencyIssue.figureType == 'TABLE'
                        )

        }
    }
    createComplianceIssue(complianceIssue: any) {

        return new ComplianceIssue(complianceIssue.issueId, complianceIssue.explanation, complianceIssue.phraseId);
    }

}